import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { getDashboardStats } from "../../lib/supabase/dashboard";

import { FaBoxesStacked, FaPlus } from "react-icons/fa6";
import { FaBoxOpen } from "react-icons/fa";
import { RiCalendarScheduleFill, RiUserSettingsLine } from "react-icons/ri";
import { MdManageAccounts } from "react-icons/md";
import {
    FiCheckCircle, FiClock, FiAlertTriangle,
    FiTrendingUp, FiRefreshCw, FiArrowRight, FiZap, FiBarChart2,
    FiUsers, FiChevronRight,
    FiDollarSign, FiSend, FiCalendar,
} from 'react-icons/fi';

import Skeleton from 'react-loading-skeleton';
import { hasPermission } from "../../lib/permissions";
import { Truck } from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
};

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

const timeAgo = (date) => {
    if (!date) return '';
    const d = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (d < 60) return 'just now';
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
    return new Date(date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

// ─── Config ───────────────────────────────────────────────────────────────────

const STAT_CONFIG = [
    { key: 'activeJobs', label: 'Active Jobs', Icon: FaBoxOpen },
    { key: 'completedJobs', label: 'Completed', Icon: FiCheckCircle },
    { key: 'pendingJobs', label: 'Pending', Icon: FiClock },
    { key: 'totalUsers', label: 'Team', Icon: MdManageAccounts },
    { key: 'urgentJobs', label: 'Urgent', Icon: FiAlertTriangle, danger: true },
    { key: 'thisWeekJobs', label: 'This Week', Icon: FiTrendingUp },
];

const STATUS_META = {
    pending_design: { label: 'Pending Design', dot: 'bg-blue-400', bar: 'bg-blue-400' },
    pending_estimation: { label: 'Pending Estimate', dot: 'bg-violet-400', bar: 'bg-violet-400' },
    in_progress: { label: 'In Progress', dot: 'bg-amber-400', bar: 'bg-amber-400' },
    ready_for_delivery: { label: 'Ready for Delivery', dot: 'bg-teal-400', bar: 'bg-teal-400' },
    completed: { label: 'Completed', dot: 'bg-gray-400', bar: 'bg-gray-400' },
};

const JOB_TYPE_LABEL = {
    'Design': 'Design',
    'Estimation': 'Estimate',
    'Design & Estimation': 'D & E',
    'Timber': 'Timber',
};

const ROLE_BADGE = {
    super_admin: 'bg-amber-50 text-amber-700 border-amber-200',
    admin: 'bg-blue-50  text-blue-700  border-blue-200',
    designer: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    estimator: 'bg-purple-50  text-purple-700  border-purple-200',
};

// ─── Status dot helpers ───────────────────────────────────────────────────────

const dotDesign = (s) => s === 'completed' ? 'bg-emerald-400' : (s === 'in_progress' || s === 'waiting_design') ? 'bg-amber-400' : 'bg-red-400';
const dotEstimation = (s) => s === 'completed' ? 'bg-emerald-400' : s === 'in_progress' ? 'bg-amber-400' : 'bg-red-400';
const dotDelivery = (s) => s === 'delivered' ? 'bg-emerald-400' : (s === 'in_transit' || s === 'scheduled') ? 'bg-amber-400' : 'bg-red-400';
const dotProduction = (s) => s === 'completed' ? 'bg-emerald-400' : ['in_cutting', 'cutting_ended', 'in_production'].includes(s) ? 'bg-amber-400' : 'bg-red-400';

const aggDot = (statuses, colorFn) => {
    if (!statuses.length) return 'bg-gray-300';
    if (statuses.every(s => colorFn(s) === 'bg-emerald-400')) return 'bg-emerald-400';
    if (statuses.some(s => colorFn(s) === 'bg-amber-400')) return 'bg-amber-400';
    return 'bg-red-400';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">{children}</p>
);

// Stat tile — mirrors ScheduleHub floating card style
const StatTile = ({ label, Icon, value, danger, loading }) => (
    <div className="dashboard-stat bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${danger ? 'bg-red-50' : 'bg-gray-100'}`}>
            <Icon className={`w-4 h-4 ${danger ? 'text-red-500' : 'text-gray-500'}`} />
        </div>
        <div>
            <div className="text-2xl font-bold text-zinc-700 leading-none">
                {loading ? <Skeleton width={36} height={26} /> : (value ?? '—')}
            </div>
            <div className="text-[11px] text-gray-400 mt-1">{label}</div>
        </div>
    </div>
);

// Action tile — floating hub card
const ActionTile = ({ Icon, label, desc, onClick }) => (
    <button
        onClick={onClick}
        className="dashboard-action group bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 text-left p-4 flex flex-col gap-3"
    >
        <div className="flex items-center justify-between">
            <div className="w-9 h-9 rounded-lg bg-zinc-700 flex items-center justify-center flex-shrink-0">
                <Icon size={15} className="text-white" />
            </div>
            <FiChevronRight size={13} className="text-gray-200 group-hover:text-gray-400 transition-colors" />
        </div>
        <div>
            <p className="text-sm font-bold text-zinc-700 leading-snug">{label}</p>
            <p className="text-[11px] text-gray-400 mt-0.5 leading-relaxed">{desc}</p>
        </div>
    </button>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminDashboard = () => {
    const { userProfile, company } = useAuth();
    const [stats, setStats] = useState({
        activeJobs: null, completedJobs: null, pendingJobs: null,
        urgentJobs: null, totalUsers: null, thisWeekJobs: null,
        statusBreakdown: null, recentJobs: null,
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const navigate = useNavigate();
    const { companySlug } = useParams();

    useEffect(() => {
        if (company?.id) loadStats(false);
    }, [company]);

    const loadStats = async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);
            const data = await getDashboardStats(company.id);
            setStats(data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Failed to fetch dashboard stats:', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const go = (route) => navigate(`/${companySlug}/${route}`);

    const firstName = capitalize(
        userProfile?.full_name?.split(' ')[0] || userProfile?.username || ''
    );

    const roleBadgeText = [
        userProfile?.position?.toLowerCase() !== 'admin' ? userProfile?.position : null,
        userProfile?.role ? capitalize(userProfile.role.replace(/_/g, ' ')) : null,
    ].filter(Boolean).join(' · ');

    const relativeTime = lastUpdated ? (() => {
        const d = Math.floor((Date.now() - lastUpdated.getTime()) / 1000);
        if (d < 30) return 'just now';
        if (d < 60) return `${d}s ago`;
        if (d < 3600) return `${Math.floor(d / 60)}m ago`;
        return `${Math.floor(d / 3600)}h ago`;
    })() : null;

    const maxBreakdownCount = Math.max(
        ...((stats.statusBreakdown || []).map(s => s.count)),
        1
    );

    const ACTIONS = [
        {
            id: 'new',
            roles: ['super_admin', 'admin'],
            route: 'assignment/new', Icon: FaPlus,
            label: 'New Assignment',
            desc: 'Create and assign new jobs'
        },
        {
            id: 'my-asgn',
            roles: ['designer', 'estimator'],
            permissions: ['SEEPAGE_MYJOBS'],
            route: 'assignment/my-assignments',
            Icon: FaBoxOpen,
            label: 'My Assignments',
            desc: 'Monitor your assignments'
        },
        {
            id: 'my-del',
            roles: ['driver'],
            route: 'my-deliveries',
            Icon: Truck,
            label: 'My Deliveries',
            desc: 'View active deliveries'
        },
        {
            id: 'track',
            roles: ['super_admin', 'admin', 'designer', 'estimator'],
            route: 'assignment/list',
            Icon: FaBoxesStacked,
            label: 'Track Assignments',
            desc: 'Monitor all active jobs'
        },
        {
            id: 'customers',
            roles: ['super_admin', 'admin', 'designer', 'estimator'],
            route: 'customers',
            Icon: FiUsers,
            label: 'Customers',
            desc: 'Create and organize customers'
        },
        {
            id: 'schedule',
            roles: ['super_admin', 'admin', 'designer', 'estimator'],
            route: 'schedule',
            Icon: RiCalendarScheduleFill,
            label: 'Schedule',
            desc: 'Plan and organize tasks'
        },
        {
            id: 'users',
            roles: ['super_admin', 'admin', 'designer', 'estimator', 'driver'],
            route: 'users',
            Icon: RiUserSettingsLine,
            label: 'Users',
            desc: hasPermission(userProfile?.role, ['super_admin', 'admin']) ? 'Manage your team' : 'View team members'
        },
        {
            id: 'analytics',
            roles: ['super_admin', 'admin'],
            route: 'analytics',
            Icon: FiBarChart2,
            label: 'Analytics',
            desc: 'Reports and insights'
        },
        {
            id: 'final-pricings',
            roles: [],
            permissions: ['view_finalpricing_page'],
            route: 'final-pricings',
            Icon: FiDollarSign,
            label: 'Final Pricings',
            desc: 'Review and enter final job prices'
        },
        {
            id: 'final-pricing-calendar',
            roles: [],
            permissions: ['view_finalpricing_page'],
            route: 'calendar/final-pricing',
            Icon: FiCalendar,
            label: 'Final Pricing Calendar',
            desc: 'View final pricing by delivery date'
        },
        {
            id: 'quotations',
            roles: [],
            permissions: ['view_quotation_page'],
            route: 'quotations',
            Icon: FiSend,
            label: 'Quotation',
            desc: 'Email final pricing quotes to customers'
        }
    ] // .filter(a => hasPermission(userProfile?.role, a.roles));

    const isActionVisible = a => {
        const roles = a.roles || [];
        const perms = a.permissions || [];
        if (roles.length === 0 && perms.length === 0) return true;
        const roleOk = roles.length > 0 && hasPermission(userProfile?.role, roles);
        const permOk = perms.length > 0 && perms.some(p => userProfile?.permissions?.includes(p));
        return roleOk || permOk;
    };

    const ACTIONS_VISIBLE = ACTIONS.filter(isActionVisible);

    const showUrgentBanner = !loading && (stats.urgentJobs ?? 0) > 0;

    return (
        <div className="company-dashboard flex flex-col gap-7">

            {/* ── Header ───────────────────────────────────────────────────── */}
            <div className="dashboard-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-6 border-b border-gray-100">

                {/* Left side */}
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex-shrink-0 overflow-hidden bg-zinc-700 flex items-center justify-center shadow-sm">
                        {userProfile?.avatar_url ? (
                            <img src={userProfile.avatar_url} alt={firstName} className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-sm sm:text-base font-bold text-white select-none">
                                {(userProfile?.full_name || userProfile?.username || '?')[0].toUpperCase()}
                            </span>
                        )}
                    </div>

                    <div className="min-w-0">
                        <h1 className="text-lg sm:text-2xl font-bold text-zinc-700 truncate">
                            {getGreeting()}, {firstName}
                        </h1>

                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                            <p className="text-xs sm:text-sm text-gray-400">
                                {new Date().toLocaleDateString('en-AU', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                })}
                            </p>

                            {company?.name && (
                                <>
                                    <span className="text-gray-200 hidden sm:inline">·</span>
                                    <span className="text-xs sm:text-sm text-gray-400 truncate">
                                        {company.name}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right side */}
                <div className="dashboard-identity flex flex-col items-start sm:items-center justify-between sm:flex-row gap-3">
                    <div className={`px-2.5 py-1 rounded-md text-xs font-semibold border whitespace-nowrap ${ROLE_BADGE[userProfile?.role] || 'bg-gray-50 text-gray-600 border-gray-200'
                        }`}>
                        {roleBadgeText}
                    </div>

                    <div className="flex items-center gap-1.5 ml-2 sm:m-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        <span className="text-xs text-gray-400">Online</span>
                    </div>
                </div>
            </div>

            {/* ── Stats ────────────────────────────────────────────────────── */}
            <div>
                <div className="dashboard-section-heading flex items-center justify-between mb-3">
                    <SectionLabel>Overview</SectionLabel>
                    <button
                        onClick={() => loadStats(true)}
                        disabled={loading || refreshing}
                        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-40 mb-3"
                    >
                        <FiRefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
                        {relativeTime ? `Updated ${relativeTime}` : 'Refresh'}
                    </button>
                </div>
                <div className="dashboard-stats grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {STAT_CONFIG.map(({ key, label, Icon, danger }) => (
                        <StatTile
                            key={key}
                            label={label}
                            Icon={Icon}
                            value={stats[key]}
                            danger={danger}
                            loading={loading}
                        />
                    ))}
                </div>
            </div>

            {/* ── Quick Actions ─────────────────────────────────────────────── */}
            <div>
                <SectionLabel>Quick Actions</SectionLabel>
                <div className="dashboard-actions grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {ACTIONS_VISIBLE.map(({ id, route, Icon, label, desc }) => (
                        <ActionTile
                            key={id}
                            Icon={Icon}
                            label={label}
                            desc={desc}
                            onClick={() => go(route)}
                        />
                    ))}
                </div>
            </div>

            {/* ── Content panels ────────────────────────────────────────────── */}
            <div className="flex flex-col gap-4">

                {/* Urgent banner */}
                {showUrgentBanner && (
                    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center gap-2.5">
                            <FiZap className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <span className="text-sm text-red-700 font-medium">
                                {stats.urgentJobs} urgent job{stats.urgentJobs !== 1 ? 's' : ''} need{stats.urgentJobs === 1 ? 's' : ''} attention
                            </span>
                        </div>
                        <button
                            onClick={() => go('assignment/list')}
                            className="text-xs font-semibold text-red-600 hover:text-red-700 flex items-center gap-1 flex-shrink-0 transition-colors"
                        >
                            View <FiArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                )}

                {/* Recent Jobs */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                        <div>
                            <p className="text-[15px] font-bold text-zinc-700 leading-tight">Recent Jobs</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">Latest activity across all assignments</p>
                        </div>
                        {hasPermission(userProfile?.role, ['super_admin', 'admin', 'designer', 'estimator']) && (
                            <button
                                onClick={() => go('assignment/list')}
                                className="flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-zinc-900 transition-colors"
                            >
                                View all <FiArrowRight className="w-3 h-3" />
                            </button>
                        )}
                    </div>

                    {/* Column labels */}
                    <div className="dashboard-job-columns px-5 py-2 bg-gray-50 border-b border-gray-100 grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 items-center">
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest w-14">ID</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Customer</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest hidden sm:block">Type</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest hidden xl:block w-16 text-right">When</span>
                        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Status</span>
                    </div>

                    <div className="divide-y divide-gray-50">
                        {loading ? (
                            [...Array(6)].map((_, i) => (
                                <div key={i} className="px-5 py-3.5 flex items-center gap-3">
                                    <Skeleton width={48} height={13} />
                                    <Skeleton width="40%" height={13} />
                                    <Skeleton width={48} height={13} />
                                </div>
                            ))
                        ) : (stats.recentJobs || []).length === 0 ? (
                            <div className="px-5 py-12 text-center">
                                <p className="text-sm font-semibold text-gray-500 mb-1">No jobs yet</p>
                                <p className="text-xs text-gray-400">Jobs will appear here once created</p>
                            </div>
                        ) : (
                            (stats.recentJobs || []).map(job => {
                                const isUrgent = job.urgent_design || job.urgent_quote;
                                return (
                                    <button
                                        key={job.id}
                                        onClick={() => go(`assignment/${job.id}`)}
                                        className="dashboard-job-row w-full px-5 py-3.5 grid grid-cols-[auto_1fr_auto_auto_auto] gap-3 items-center hover:bg-gray-50 transition-colors text-left group"
                                    >
                                        <div className="flex items-center gap-2 w-14">
                                            {isUrgent && <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />}
                                            <span className="font-mono text-xs text-gray-400 truncate">
                                                #{job.unique_id}
                                            </span>
                                        </div>
                                        <span className="text-sm font-semibold text-zinc-700 truncate group-hover:text-zinc-900 transition-colors">
                                            {job.customer_name || '—'}
                                        </span>
                                        <span className="text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md flex-shrink-0 hidden sm:block">
                                            {JOB_TYPE_LABEL[job.job_type] || job.job_type || '—'}
                                        </span>
                                        <span className="text-xs text-gray-400 flex-shrink-0 hidden xl:block w-16 text-right">
                                            {timeAgo(job.created_at)}
                                        </span>

                                        {/* Status dots */}
                                        {(() => {
                                            const quotes = job.job_quotes || [];
                                            const required = quotes.filter(q => q.is_required);
                                            const hasEst = job.job_type?.includes('Estimation');
                                            const hasDes = job.job_type?.includes('Design');
                                            const designQuotes = required.filter(q => q.design_status && q.design_status !== 'not_required');
                                            const estStatuses = required.map(q => q.estimation_status).filter(s => s && s !== 'not_required');
                                            const delStatuses = required.map(q => q.delivery_status).filter(Boolean);
                                            const prodStatuses = required.map(q => q.production_status).filter(s => s && s !== 'not_required');

                                            if (!hasEst && !hasDes) {
                                                const meta = STATUS_META[job.main_status];
                                                return (
                                                    <div className="flex items-center gap-1 flex-shrink-0">
                                                        <div className={`w-2 h-2 rounded-full ${meta?.dot || 'bg-gray-300'}`} />
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div className="flex items-center gap-2.5 flex-shrink-0">
                                                    {hasEst && (
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Est</span>
                                                            <div className={`w-2 h-2 rounded-full ${aggDot(estStatuses, dotEstimation)}`} />
                                                        </div>
                                                    )}
                                                    {hasDes && designQuotes.length > 0 && (
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Des</span>
                                                            <div className="flex gap-0.5">
                                                                {designQuotes.map(q => (
                                                                    <div key={q.id} className={`w-2 h-2 rounded-full ${dotDesign(q.design_status)}`} />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {hasDes && (
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Del</span>
                                                            <div className={`w-2 h-2 rounded-full ${aggDot(delStatuses, dotDelivery)}`} />
                                                        </div>
                                                    )}
                                                    {hasDes && (
                                                        <div className="flex items-center gap-1">
                                                            <span className="text-[10px] text-gray-400 uppercase tracking-wide">Prod</span>
                                                            <div className={`w-2 h-2 rounded-full ${aggDot(prodStatuses, dotProduction)}`} />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })()}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Bottom row: Breakdown + This Week */}
                <div className="grid grid-cols-1 sm:grid-cols-[3fr_2fr] gap-4">

                    {/* Active Breakdown */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3.5 border-b border-gray-100">
                            <p className="text-[15px] font-bold text-zinc-700 leading-tight">Active Breakdown</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">Jobs by current status</p>
                        </div>
                        <div className="p-5 space-y-4">
                            {loading ? (
                                [...Array(4)].map((_, i) => (
                                    <div key={i} className="space-y-1.5">
                                        <Skeleton height={12} width="60%" />
                                        <Skeleton height={6} borderRadius={999} />
                                    </div>
                                ))
                            ) : (stats.statusBreakdown || []).every(s => s.count === 0) ? (
                                <div className="py-6 text-center text-sm text-gray-400">No active jobs</div>
                            ) : (
                                (stats.statusBreakdown || []).map(({ key, label, count }) => {
                                    const meta = STATUS_META[key];
                                    const pct = Math.round((count / maxBreakdownCount) * 100);
                                    return (
                                        <div key={key}>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${meta?.dot}`} />
                                                    <span className="text-xs text-gray-600">{label}</span>
                                                </div>
                                                <span className="text-xs font-semibold text-zinc-700 tabular-nums">{count}</span>
                                            </div>
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full ${meta?.bar} transition-all duration-500`}
                                                    style={{ width: count === 0 ? '0%' : `${Math.max(pct, 3)}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        {!loading && (stats.activeJobs ?? 0) > 0 && (
                            <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                                <span className="text-xs text-gray-400">Total active</span>
                                <span className="text-xs font-semibold text-zinc-700 tabular-nums">{stats.activeJobs}</span>
                            </div>
                        )}
                    </div>

                    {/* This Week */}
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col justify-between">
                        <SectionLabel>This Week</SectionLabel>
                        <div>
                            <div className="text-5xl font-bold text-zinc-700 leading-none">
                                {loading ? <Skeleton width={56} height={52} /> : (stats.thisWeekJobs ?? '—')}
                            </div>
                            <div className="text-sm text-gray-400 mt-2">
                                new job{stats.thisWeekJobs !== 1 ? 's' : ''} added
                            </div>
                        </div>
                        <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                            <span className="text-xs text-gray-400">Team size</span>
                            <span className="text-sm font-bold text-zinc-700">
                                {loading ? <Skeleton width={24} height={16} /> : (stats.totalUsers ?? '—')}
                            </span>
                        </div>
                    </div>

                </div>
            </div>

        </div>
    );
};

export default AdminDashboard;
